/**
 * Flight history and replay.
 *
 * The history tables are read straight out of SQLite, so the server hands back
 * the column names: snake_case, and nullable wherever the aircraft did not
 * report. That boundary is converted here, once, into the camelCase the rest of
 * the app speaks. The predecessor let raw rows through to the components, and
 * `session.total_distance` versus `session.totalDistance` was live in two
 * different screens at the same time, one of them always showing nothing.
 *
 * A null stays null through the conversion. A flight with no recorded maximum
 * altitude and a flight that never climbed are different facts, and defaulting
 * to zero here would erase the difference before anything could render it.
 */

import { api, type ApiResult } from '@/api/client';
import type { DroneId } from '@/types/domain';

interface FlightSessionRow {
  session_id: string;
  drone_id: string;
  start_time: number;
  end_time: number | null;
  max_altitude: number | null;
  max_speed: number | null;
  max_distance: number | null;
  total_distance: number | null;
  flight_time: number | null;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  mission_name: string | null;
  waypoints_completed: number | null;
  total_waypoints: number | null;
  warnings: number | null;
  errors: number | null;
  geofence_breaches: number | null;
  pilot: string | null;
  notes: string | null;
  notes_by?: string | null;
  notes_at?: number | null;
  team_id?: string | null;
  team_callsign?: string | null;
  vehicle_class?: string | null;
  end_reason: string | null;
  end_detail: string | null;
  // Rolled up from the extended telemetry when the flight ends. Absent on a
  // server older than the migration that added them, hence optional here.
  wh_used?: number | null;
  mah_used?: number | null;
  avg_wind?: number | null;
  max_wind?: number | null;
  avg_ambient_temp?: number | null;
  max_vibe?: number | null;
  clipping_events?: number | null;
  max_ekf_variance?: number | null;
  worst_drop_rate?: number | null;
}

export interface FlightSession {
  sessionId: string;
  droneId: DroneId;
  /** Unix seconds, as recorded by the server. */
  startTime: number;
  endTime: number | null;
  maxAltitudeM: number | null;
  maxSpeedMs: number | null;
  maxDistanceM: number | null;
  totalDistanceM: number | null;
  flightTimeS: number | null;
  startLat: number | null;
  startLon: number | null;
  endLat: number | null;
  endLon: number | null;
  missionName: string | null;
  waypointsCompleted: number | null;
  totalWaypoints: number | null;
  warnings: number;
  errors: number;
  geofenceBreaches: number;
  pilot: string | null;
  notes: string | null;
  /** Username of whoever last wrote the notes, and when (Unix seconds). */
  notesBy: string | null;
  notesAt: number | null;
  /**
   * The team that held the aircraft when it flew, stamped at the start. A
   * drone can move teams afterwards and this still names who flew it. Null for
   * flights recorded before the stamp existed, or by a drone on no team.
   */
  teamId: string | null;
  teamCallsign: string | null;
  /** copter, plane, rover, boat... as the autopilot reported it at arm. */
  vehicleClass: string | null;
  endReason: string | null;
  endDetail: string | null;
  /** Energy the flight drew, from the autopilot's own integrated counter. */
  whUsed: number | null;
  mahUsed: number | null;
  avgWindMs: number | null;
  maxWindMs: number | null;
  avgAmbientTempC: number | null;
  /** Worst single axis, m/s^2. Over 30 upsets the EKF; over 60 loses position hold. */
  maxVibe: number | null;
  clippingEvents: number | null;
  /** Worst EKF variance ratio. 1.0 is where the autopilot rejects the estimate. */
  maxEkfVariance: number | null;
  /** 0-1 fraction of packets lost at the worst moment. */
  worstDropRate: number | null;
  /** True while the aircraft is still flying this session. */
  inProgress: boolean;
}

function toSession(row: FlightSessionRow): FlightSession {
  return {
    sessionId: row.session_id,
    droneId: row.drone_id,
    startTime: row.start_time,
    endTime: row.end_time,
    maxAltitudeM: row.max_altitude,
    maxSpeedMs: row.max_speed,
    maxDistanceM: row.max_distance,
    totalDistanceM: row.total_distance,
    flightTimeS: row.flight_time,
    startLat: row.start_lat,
    startLon: row.start_lon,
    endLat: row.end_lat,
    endLon: row.end_lon,
    missionName: row.mission_name,
    waypointsCompleted: row.waypoints_completed,
    totalWaypoints: row.total_waypoints,
    warnings: row.warnings ?? 0,
    errors: row.errors ?? 0,
    geofenceBreaches: row.geofence_breaches ?? 0,
    pilot: row.pilot,
    notes: row.notes,
    notesBy: row.notes_by ?? null,
    notesAt: row.notes_at ?? null,
    teamId: row.team_id ?? null,
    teamCallsign: row.team_callsign ?? null,
    vehicleClass: row.vehicle_class ?? null,
    endReason: row.end_reason,
    endDetail: row.end_detail,
    whUsed: row.wh_used ?? null,
    mahUsed: row.mah_used ?? null,
    avgWindMs: row.avg_wind ?? null,
    maxWindMs: row.max_wind ?? null,
    avgAmbientTempC: row.avg_ambient_temp ?? null,
    maxVibe: row.max_vibe ?? null,
    clippingEvents: row.clipping_events ?? null,
    maxEkfVariance: row.max_ekf_variance ?? null,
    worstDropRate: row.worst_drop_rate ?? null,
    inProgress: row.end_time === null,
  };
}

export interface TelemetryPoint {
  timestamp: number;
  msgType: string;
  lat: number | null;
  lon: number | null;
  altM: number | null;
  relativeAltM: number | null;
  headingDeg: number | null;
  groundSpeedMs: number | null;
  airSpeedMs: number | null;
  climbRateMs: number | null;
  batteryVoltage: number | null;
  batteryCurrent: number | null;
  batteryPercent: number | null;
  armed: boolean | null;
  satellites: number | null;
}

interface TelemetryRow {
  timestamp: number;
  msg_type: string;
  lat: number | null;
  lon: number | null;
  alt: number | null;
  relative_alt: number | null;
  heading: number | null;
  groundspeed: number | null;
  airspeed: number | null;
  climb_rate: number | null;
  battery_voltage: number | null;
  battery_current: number | null;
  battery_remaining: number | null;
  armed: number | null;
  satellites: number | null;
}

function toPoint(row: TelemetryRow): TelemetryPoint {
  return {
    timestamp: row.timestamp,
    msgType: row.msg_type,
    lat: row.lat,
    lon: row.lon,
    altM: row.alt,
    relativeAltM: row.relative_alt,
    headingDeg: row.heading,
    groundSpeedMs: row.groundspeed,
    airSpeedMs: row.airspeed,
    climbRateMs: row.climb_rate,
    batteryVoltage: row.battery_voltage,
    batteryCurrent: row.battery_current,
    batteryPercent: row.battery_remaining,
    armed: row.armed === null ? null : row.armed === 1,
    satellites: row.satellites,
  };
}

export interface FlightEvent {
  timestamp: number;
  eventType: string;
  severity: string;
  message: string | null;
  lat: number | null;
  lon: number | null;
  altM: number | null;
}

interface EventRow {
  timestamp: number;
  event_type: string;
  severity: string;
  message: string | null;
  lat: number | null;
  lon: number | null;
  alt: number | null;
}

function toEvent(row: EventRow): FlightEvent {
  return {
    timestamp: row.timestamp,
    eventType: row.event_type,
    severity: row.severity,
    message: row.message,
    lat: row.lat,
    lon: row.lon,
    altM: row.alt,
  };
}

export interface FlightStatistics {
  totalFlights: number;
  totalFlightTimeHours: number;
  avgFlightTimeMinutes: number;
  highestAltitudeM: number;
  fastestSpeedMs: number;
  totalDistanceKm: number;
  furthestFromLaunchM: number;
  totalWarnings: number;
  totalErrors: number;
  totalGeofenceBreaches: number;
}

interface StatisticsWire {
  total_flights: number;
  total_flight_time_hours: number;
  avg_flight_time_minutes: number;
  highest_altitude_m: number;
  fastest_speed_ms: number;
  total_distance_km: number;
  furthest_from_launch_m: number;
  total_warnings: number;
  total_errors: number;
  total_geofence_breaches: number;
}

export async function listFlights(options: {
  droneId?: DroneId;
  limit?: number;
  offset?: number;
} = {}): Promise<ApiResult<{ sessions: FlightSession[]; activeByDrone: Record<string, string> }>> {
  const result = await api.get<{ sessions: FlightSessionRow[]; active: Record<string, string> }>(
    '/flights',
    {
      logged: false,
      query: {
        droneId: options.droneId,
        limit: options.limit ?? 100,
        offset: options.offset ?? 0,
      },
    },
  );
  if (!result.ok) return result;
  return {
    ...result,
    data: {
      sessions: result.data.sessions.map(toSession),
      activeByDrone: result.data.active ?? {},
    },
  };
}

export async function getFlight(sessionId: string): Promise<ApiResult<FlightSession>> {
  const result = await api.get<FlightSessionRow>(
    `/flights/${encodeURIComponent(sessionId)}`,
    { logged: false },
  );
  return result.ok ? { ...result, data: toSession(result.data) } : result;
}

export async function getFlightTelemetry(
  sessionId: string,
  options: { msgType?: string; limit?: number } = {},
): Promise<ApiResult<TelemetryPoint[]>> {
  const result = await api.get<{ telemetry: TelemetryRow[] }>(
    `/flights/${encodeURIComponent(sessionId)}/telemetry`,
    {
      logged: false,
      query: {
        // Position is what a track and an altitude trace are made of. Asking
        // for every message type pulls tens of thousands of attitude rows a
        // chart would immediately throw away.
        msgType: options.msgType ?? 'GLOBAL_POSITION_INT',
        limit: options.limit ?? 20_000,
      },
    },
  );
  return result.ok ? { ...result, data: result.data.telemetry.map(toPoint) } : result;
}

export async function getFlightEvents(sessionId: string): Promise<ApiResult<FlightEvent[]>> {
  const result = await api.get<{ events: EventRow[] }>(
    `/flights/${encodeURIComponent(sessionId)}/events`,
    { logged: false },
  );
  return result.ok ? { ...result, data: result.data.events.map(toEvent) } : result;
}

export async function getFlightStatistics(
  droneId?: DroneId,
): Promise<ApiResult<FlightStatistics>> {
  const result = await api.get<StatisticsWire>('/flights/statistics', {
    logged: false,
    query: { droneId },
  });
  if (!result.ok) return result;
  const wire = result.data;
  return {
    ...result,
    data: {
      totalFlights: wire.total_flights,
      totalFlightTimeHours: wire.total_flight_time_hours,
      avgFlightTimeMinutes: wire.avg_flight_time_minutes,
      highestAltitudeM: wire.highest_altitude_m,
      fastestSpeedMs: wire.fastest_speed_ms,
      totalDistanceKm: wire.total_distance_km,
      furthestFromLaunchM: wire.furthest_from_launch_m,
      totalWarnings: wire.total_warnings,
      totalErrors: wire.total_errors,
      totalGeofenceBreaches: wire.total_geofence_breaches,
    },
  };
}

/** The server caps notes here too and refuses anything longer with a 422. */
export const MAX_NOTES_CHARS = 4000;

export interface FlightNotes {
  sessionId: string;
  notes: string | null;
  notesBy: string | null;
  notesAt: number | null;
}

/** Replace a flight's debrief notes. Blank text clears them. */
export function setFlightNotes(sessionId: string, notes: string): Promise<ApiResult<FlightNotes>> {
  return api.put<FlightNotes>(
    `/flights/${encodeURIComponent(sessionId)}/notes`,
    { notes },
    { label: 'FLIGHT NOTES' },
  );
}

export function deleteFlight(sessionId: string): Promise<ApiResult<{ deleted: string }>> {
  return api.delete<{ deleted: string }>(`/flights/${encodeURIComponent(sessionId)}`);
}

export type ReplayState = 'stopped' | 'playing' | 'paused';

export interface ReplayStatus {
  state: ReplayState;
  sessionId: string | null;
  droneId: DroneId | null;
  /** Seconds from the start of the session. */
  offset: number;
  duration: number;
  speed: number;
  framesEmitted: number;
  frameCount: number;
}

/**
 * Replay drives the live telemetry path, so a replayed aircraft appears in the
 * fleet and on the map exactly like a flying one. That is deliberate on the
 * server side, and it is why the flights screen shows a clear banner: an
 * operator has to be able to tell a recording from an aircraft.
 */
export function getReplayStatus(): Promise<ApiResult<ReplayStatus>> {
  return api.get<ReplayStatus>('/flights/replay', { logged: false });
}

export function loadReplay(sessionId: string): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>(
    `/flights/${encodeURIComponent(sessionId)}/replay/load`,
    undefined,
    { label: `REPLAY LOAD ${sessionId}` },
  );
}

export function playReplay(): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/play', undefined, { label: 'REPLAY PLAY' });
}

export function pauseReplay(): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/pause', undefined, { label: 'REPLAY PAUSE' });
}

export function stopReplay(): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/stop', undefined, { label: 'REPLAY STOP' });
}

/**
 * Drop the loaded session entirely, removing the impersonated aircraft from the
 * live fleet. Stop only halts playback; this is what the Flights UNLOAD does.
 */
export function unloadReplay(): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/unload', undefined, { label: 'REPLAY UNLOAD' });
}

export function seekReplay(offsetS: number): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/seek', { offset: offsetS }, { logged: false });
}

/** The server caps this at 8x, so the UI offers nothing it would refuse. */
export const REPLAY_SPEEDS = [0.5, 1, 2, 4, 8] as const;

export function setReplaySpeed(speed: number): Promise<ApiResult<ReplayStatus>> {
  return api.post<ReplayStatus>('/flights/replay/speed', { speed }, { logged: false });
}
